import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'
import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useReporting from '@hooks/useReporting'

import GenerateReportModal from '@modals/GenerateReportModal'

import PageContent from './components/PageContent'

const defaultState = {
  reportFormState: {
    end_date: '',
    reportKey: '' as 'template_rejections' | 'first_time_right_leaderboard' | 'active_projects' | '',
    start_date: '',
  },
  showGenerateReportModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

export type PageContextPayload = {
  callbacks: {
    showGenerateReportModal: (payload: object) => void,
  },
}

const callbacks = (component: string, setState: SetState) => {
  const componentCallbacks = {
    GenerateReportModal: {
      closeModal: () => setState({ showGenerateReportModal: false }),
      generateTeamPerformanceReport: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateReportFormState: (formState: any) => setState({ reportFormState: formState }),
    },
  }

  // @ts-expect-error - Dynamic component lookup
  return componentCallbacks[component as any]
}

const Overview = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    reportFormState,
    showGenerateReportModal,
  } = state

  const reportingPayload = useReporting({
    allowedReports: reportFormState.reportKey ? [reportFormState.reportKey as any] : [],
    defaultValues: {
      chart_data_key: reportFormState.reportKey || 'first_time_right_leaderboard',
      end_date: reportFormState.end_date || undefined,
      start_date: reportFormState.start_date || undefined,
    },
    formDeps: [reportFormState.reportKey, reportFormState.end_date, reportFormState.start_date],
  })

  const {
    callbacks: { generateReport },
    loading,
    saveEnabled,
  } = reportingPayload

  const modalContext = useModals()

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showGenerateReportModal: () => {
        setState({ showGenerateReportModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        <GenerateReportModal
          callbacks={callbacks('GenerateReportModal', setState) as any}
          formState={reportFormState}
          generateReport={generateReport}
          loading={loading}
          saveEnabled={saveEnabled}
          showModal={showGenerateReportModal}
        />

      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Overview
